// TanStack Query client — the shared client-side cache for Stride.
//
// WHY THIS EXISTS
// Before this, every component fetched in its own useEffect with no shared
// cache: `getUsers` was called from 13 independent call sites, `getFields`
// from 9, `getProjects` from 7. Opening a work-item modal re-pulled the whole
// reference set every time. modules/folders/server/request-coalescer.js was
// written to absorb the server-side symptom of the same gap (identical
// concurrent requests from double tab mounts doubling 50s loads) — this
// stops those duplicates leaving the browser at all.
//
// The defaults below are deliberately tuned for THIS codebase, not library
// defaults. Read the comments before changing them.

import { QueryClient } from '@tanstack/react-query';
import { shouldRetry } from './policy';

/**
 * staleTime tiers. Picked against the server's own caching so staleness
 * doesn't silently compound: modules/database/work-items/server/edge-cache.js
 * already serves stale-while-revalidate on a 10-minute TTL, so a generous
 * client staleTime on top of it stacks (client + server) into data that can
 * be much older than either number suggests. Keep REFERENCE below the edge
 * cache's TTL and never apply it to work-item payloads.
 */
export const STALE = {
  /** Schema-level metadata: fields, statuses, priorities, link types, work
   *  types. Changes only when an admin edits configuration. */
  REFERENCE: 10 * 60 * 1000,
  /** Directory data: users, groups, spaces. Changes when people or spaces
   *  are provisioned — rare, but more often than schema. */
  DIRECTORY: 5 * 60 * 1000,
  /** Typeahead result sets. Long enough to make backtracking a keystroke
   *  instant, short enough that a stale hit is never surprising. */
  SEARCH: 60 * 1000,
  /** Dashboard card payloads (ZSD aggregate / segment / timeseries).
   *
   *  Chosen because dashboards have NO auto-refresh timer — refreshNonce
   *  (page "refresh all") and refreshTick (a card's "Refresh data") are both
   *  user-driven, and both bypass staleTime via refetch(). So the only thing
   *  this number governs is how long a repeated view of the SAME filter
   *  combination is served from memory. 60s covers the toggle-a-filter-and-
   *  toggle-back pattern — which on a 16-card dashboard is ~32 ZQL
   *  aggregations over 213k work items — without letting a monitoring screen
   *  drift meaningfully out of date. */
  DASHBOARD: 60 * 1000,
  /** Work-item data. Socket-invalidated (see QueryProvider), so it must
   *  never be considered fresh on its own. */
  LIVE: 0,
};

/** Garbage-collect cached-but-unmounted data after 30 min. */
const GC_TIME = 30 * 60 * 1000;

export function createQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: STALE.REFERENCE,
        gcTime: GC_TIME,
        retry: shouldRetry,
        retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 10_000),

        // OFF deliberately. 57 components fetch on mount; with focus
        // refetching on, every alt-tab back into the app would fan out a
        // refetch per mounted query. That is the same amplification shape
        // as the reconnect storm diagnosed in d591327.
        refetchOnWindowFocus: false,

        // OFF deliberately, and for a documented reason: d591327 found that
        // Socket.IO reconnects were making clients "refetch every container",
        // self-amplifying into a storm that stalled the event loop and caused
        // more reconnects. Reconnect-driven refresh is handled intentionally
        // by the socket invalidation in QueryProvider, not blanket-refetched
        // here.
        refetchOnReconnect: false,

        // Remounting a component should show cached data immediately and
        // revalidate only if stale — that is the whole point of the cache.
        refetchOnMount: true,
      },
      mutations: {
        // Writes are never auto-retried: the work-item handlers record
        // change-log entries and emit event-bus events, so a blind retry of
        // a request that actually succeeded would double-log.
        retry: false,
      },
    },
  });
}
